import jwtDecode from 'jwt-decode';
import { SplashScreen } from '@capacitor/splash-screen';
import { IAsyncSmartStorageProvider, AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { AuthenticationClient } from '@innerhive-internal/innerhive-api-client';
import { Logger } from '../support/Logger';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { UserFacingError } from '../innerhive-plus/revenue-cat/UserFacingError';
import { AnonymousClientStorageProvider } from './anonymous/CompositeAnonymousHttpClient';
import { NetworkAdapter } from '../support/NetworkAdapter';

const REDIRECT_PATH = '/oauth/code';

// Long enough for a healthy revoke to land, short enough that a hung one does not strand the user
// on a signed-out screen. See revokeWithinTimeout for what losing the race costs.
const REVOKE_TIMEOUT_MS = 3000;

type IRevokeOutcome = 'revoked' | 'failed' | 'abandoned';

export class AuthStorageProvider {
    auth: IAsyncSmartStorageProvider<{accessToken: string, refreshToken: string}>;
    previousPath: IAsyncSmartStorageProvider<string>;
    isTestUser: IAsyncSmartStorageProvider<boolean>;

    static inject = () => [AsyncStorageProvider];
    constructor(private storage: AsyncStorageProvider) {
        this.auth = this.storage.getJSONProvider('ih:auth', { userNeutral: true });
        this.previousPath = this.storage.getStringProvider('previous-path', { userNeutral: true });
        this.isTestUser = this.storage.getBooleanProvider('is-test-user',  { envNeutral: true, userNeutral: true });
    }
}

class LocationProvider {
    get = (): Location => window.location;
}

export class ViewerAuthService implements IAuthService {
    static inject = () => [CodeFlowAuthService];
    constructor(private codeFlowAuthService: CodeFlowAuthService){}
    on401Callback = async () => {
        throw new Error('Not Supported');
    };

    getToken = async () => {
        throw new Error('Not Supported');
    };

    logout = async () => {
        throw new Error('Not Supported');
    };

    login = async (params?: { returnTo?: string }) => {
        return await this.codeFlowAuthService.login(params);
    };
}

export interface IAuthService {
    getToken:  () => Promise<{ accessToken: string | undefined }>;
    on401Callback: () => Promise<void>;
    logout: () => Promise<void>;
    login: (params?: { returnTo?: string }) => Promise<never>;
}

export class CodeFlowAuthService implements IAuthService {
    static inject = () =>  [
        AuthenticationClient,
        AuthStorageProvider,
        AnonymousClientStorageProvider,
        LocationProvider,
        HistoryViewModel,
        Logger,
        BufferedEventBus,
        NetworkAdapter
    ];
    constructor(
        private authClient: AuthenticationClient,
        private storage: AuthStorageProvider,
        private anonProvider: AnonymousClientStorageProvider,
        private locationProvider: LocationProvider,
        private history: HistoryViewModel,
        private logger: Logger,
        private events: BufferedEventBus,
        private network: NetworkAdapter
    ){}

    private isOnAuthCallbackURL = () => {
        return window.location.hash.startsWith(`#${REDIRECT_PATH}`);
    };

    private getLoginRedirectURI = () => `${window.location.origin}${window.location.search}#${REDIRECT_PATH}`;

    private savePath = (path: string) => {
        this.storage.previousPath.set(path);
    };

    private popSavedPath = async () => {
        let path = await this.storage.previousPath.get();
        if (path){
            await this.storage.previousPath.remove();
        }
        else {
            path = '';
        }
        return path;
    };

    login = async ({ returnTo }: { returnTo?: string } = {}): Promise<never> => {
        if (!this.network.isOnline) {
            throw new UserFacingError({
                message: 'attempted to login while offline',
                displayMessage: `It looks like you're offline. Please try again when you have reliable internet.`,
                errorCode: 'offline',
                status: 500
            });
        }

        // need to hide splash before redirecting to the identity provider
        // TODO: revisit this when we re-implement auth flow
        setTimeout(SplashScreen.hide, 750);

        // save the current / returnTo path to be restored in loginFromAuthCallback() below
        const currentLocation = this.history.getCurrentLocation();
        this.savePath(returnTo || currentLocation.pathname + currentLocation.search);

        const isLoggingIn = await this.authClient.loginWithCodeFlow({
            redirect_uri: this.getLoginRedirectURI()
        });

        const didCancel = !isLoggingIn;

        if (didCancel){
            throw new UserFacingError({
                errorCode: 'login-aborted',
                status: 200,
                message: '',
                displayMessage: ''
            });
        }

        return new Promise(resolve => {
            // wait forever, a new page load is in progress
        });
    };

    private loginFromAuthCallbackURL = async (): Promise<{ accessToken: string }> => {
        const location = this.locationProvider.get();
        const params = new URLSearchParams(location.hash.split('?')[1]);

        this.logger.info('AuthService.loginFromAuthCallbackURL()', { location });

        // if we fail on this login callback, then the user would be stuck there
        // it is far more likely that there are error query params (that won't fix on reload)
        // or that the code is no longer valid (which won't fix on reload)
        // so we should get the user off this has before we finish or throw any errors
        this.removeHash();

        // remove path from storage here, before error handling below
        const previousPath = await this.popSavedPath();

        if (params.has('error_description') || params.has('error')) {
            const errorCode = params.get('error');
            const message = params.get('error_description');
            const err:any = new Error(`Authentication failed: ${errorCode} - ${message}`);
            err.errorCode = 'login-failed';
            throw err;
        }

        const code = params.get('code') || undefined;

        if (!code) {
            throw Error('Sorry, authentication failed. Please try again.');
        }

        try{
            const { access_token, refresh_token } = await this.authClient.exchangeCode({ code });

            this.guardAgainstViewerToken(access_token);

            this.storage.auth.set({ accessToken: access_token, refreshToken: refresh_token });

            // remove anonymous user id from storage, otherwise it will be re-used after logout
            this.anonProvider.storage.remove();

            const { userId, email } = jwtDecode(access_token) as any;

            this.events.emit('AuthService.authenticated', { userId, email });

            this.history.replace(previousPath);

            return { accessToken: access_token };

        } catch (err: any){
            // we are going to throw, causing the user to get an error page
            // they will have to reload, and hopefully it will work next time.
            this.logger.error(`AuthService.loginFromAuthCallbackURL() failed calling this.authClient.exchangeCode({ code }). Error: ${err.toString()} `);

            throw err;
        }
    };

    /**
     * To minimize complexity reasoning about user vs accountless-viewer, we don't want them to mix
     * we want to throw early at where they could, to minimize reasoning about it downstream
     */
    private guardAgainstViewerToken = (access_token: string ) => {
        const decoded = jwtDecode(access_token) as any;
        const { userId, tokenType } = decoded;

        if (!userId || tokenType === 'caremap-viewer'){
            throw new Error(`attempted to use an invalid user token ${JSON.stringify(decoded)}`);
        }
    };

    on401Callback = async () => {
        // We get here when the api returns a 401, indicating the token is too old.

        // storage may already be empty: concurrent requests each get a 401, and whichever
        // handler runs first clears it below before the others read it
        const refreshToken = (await this.storage.auth.get())?.refreshToken;
        this.removeUserFromLocalStorage();

        if (!refreshToken){
            this.logger.info('AuthService.on401Callback() - no refresh_token, reloading');

            window.location.reload();
            await new Promise(resolve => {});
            return;
        }

        try{
            const { access_token } = await this.authClient.refreshAccessToken({ refresh_token: refreshToken });

            this.storage.auth.set({ accessToken: access_token, refreshToken });

            this.logger.info('AuthService.on401Callback() - used refresh_token to get new access_token, reloading');

            window.location.reload();
            await new Promise(resolve => {});
        }
        catch (err: any){
            // this can fail under normal circumstances, eg their refresh token has been revoked
            this.logger.info(`AuthService.on401Callback() - failed calling this.authClient.refreshAccessToken(), reloading. Error ${err.toString()}`);

            window.location.reload();
            await new Promise(resolve => {});
        }
    };

    /**
     * Revokes the refresh token, but never lets sign-out wait on it indefinitely. The offline guard
     * in logout() only covers having no connection at all; a reachable but hung server would
     * otherwise hold the redirect and leave the user watching sign-out appear to do nothing.
     *
     * Losing the race is not free. The redirect unloads the page and takes the request with it, so
     * the token stays valid until it expires on its own -- which is still the better trade, since
     * the user is signed out locally either way. Both failure outcomes are reported, because a token
     * that outlives sign-out is invisible from the app: nothing looks wrong until someone uses it.
     */
    private revokeWithinTimeout = async (refreshToken: string): Promise<void> => {
        const revoke = this.authClient.revokeRefreshToken({ refresh_token: refreshToken })
            .then((): IRevokeOutcome => 'revoked')
            .catch((err: any): IRevokeOutcome => {
                // wrapped in a status-free Error, because Logger.error drops anything carrying a 4xx
                // status before it reaches Sentry, and a 400 is the likeliest rejection here. The status
                // still rides along as an extra, which that check does not inspect. Keep the stringified
                // error in the message too: once the errorCode property is gone, that marker is the only
                // thing keeping a dropped connection's fetch failure out of Sentry as noise (logout
                // rejects earlier when the app already knows it is offline, so this is the case where
                // it believed it was online and the request died anyway).
                this.logger.error(new Error(`AuthService.logout() failed revoking the refresh token: ${err?.toString()}`), { status: err?.status });
                return 'failed';
            });

        const timeout = new Promise<IRevokeOutcome>(resolve => setTimeout(() => resolve('abandoned'), REVOKE_TIMEOUT_MS));

        const outcome = await Promise.race([revoke, timeout]);

        if (outcome === 'abandoned'){
            this.logger.error(new Error('AuthService.logout() abandoned the refresh token revoke'), { timeoutMs: REVOKE_TIMEOUT_MS });
        }
    };

    logout = async () => {
        if (!this.network.isOnline) {
            throw new UserFacingError({
                message: 'attempted to logout while offline',
                displayMessage: `It looks like you're offline. Please try again when you have reliable internet.`,
                errorCode: 'offline',
                status: 500
            });
        }

        // read before clearing storage, which is where it lives
        const refreshToken = (await this.storage.auth.get())?.refreshToken;

        this.removeUserFromLocalStorage();

        // Once the revoke lands, requests on this session's access token get a 401 and on401Callback
        // reloads the page, racing the redirect below. Keep sign-out a full page load: nothing else
        // clears Sentry's user.
        if (refreshToken){
            await this.revokeWithinTimeout(refreshToken);
        }

        this.events.emit('AuthService.signing-out');

        // the login URL always asks Kinde for its own screen, so a surviving Kinde session cannot
        // silently sign anyone back in and we do not need to redirect through Kinde's logout to end
        // it. That says nothing about the upstream provider: a Google or Apple session is a cookie
        // in the user's own browser, which no identity provider can clear on our behalf.
        //
        // We must still update the path before reloading, or we'll be on the same page when we log
        // back in.
        const location = this.locationProvider.get();
        location.href = '/';

        // never resolve, we're redirecting
        await new Promise(() => {});
    };

    getToken = async (): Promise<{ accessToken: string | undefined }> => {
        const accessToken = await this.maybeGetToken();
        if (accessToken){
            return { accessToken };
        }

        await this.login();

        // if we get here, the user must have aborted login, so we can't proceed
        const error = new Error('Login aborted') as any;
        error.errorCode = 'login-aborted';
        throw error;
    };

    maybeGetToken = async (): Promise<string | undefined> => {
        if (this.isOnAuthCallbackURL()) {
            this.logger.info('AuthService.getToken() => isOnAuthCallbackURL');
            const { accessToken } = await this.loginFromAuthCallbackURL();
            return accessToken;
        }

        else if (await this.storage.auth.exists()) {
            this.logger.info('AuthService.getToken() => getTokenFromStorage');
            const { accessToken } = await this.loginFromLocalStorage();
            return accessToken;
        }
        return undefined;
    };

    private async loginFromLocalStorage(): Promise<{ accessToken: string }> {
        const result = await this.storage.auth.get();
        if (!result){
            throw new Error('loginFromLocalStorage() is only meant to be called when we know there is a token in storage');
        }

        this.guardAgainstViewerToken(result.accessToken);

        return { accessToken: result.accessToken };
    }

    private removeUserFromLocalStorage() {
        // remove token for current environment
        this.storage.auth.remove();
    };

    private removeHash = () => {
        window.history.replaceState({}, document.title, '.');
    };
}

export class AuthService implements IAuthService {
    constructor(){
        throw new Error('AuthService should have been set in IOC');
    }
    on401Callback = async () => {
        throw new Error('Not Supported');
    };

    getToken = async () => {
        throw new Error('Not Supported');
    };

    logout = async () => {
        throw new Error('Not Supported');
    };

    login = async (params?: { returnTo?: string }) => {
        throw new Error('Not Supported');
    };
}
